// Pastel palette for entity markers — single solid colour per type, designed
// to read on both dark and light basemaps. Picked to feel decent and distinct
// at small disc sizes without competing with the cluster heat colours.

// Darker mid-saturation pastels — chosen so the white Lucide glyph in the
// pin's bell stays high-contrast across both dark and light basemaps.
const PASTEL_BY_TYPE: Record<string, string> = {
  vehicle: '#4d8fb8', // cobalt blue
  car: '#4d8fb8',
  aircraft: '#3f8db1', // deep teal
  person: '#5cb087', // forest sage
  phone: '#9573c4', // deep mauve
  mobile: '#9573c4',
  mobiledevice: '#9573c4',
  btsstation: '#c89348', // mustard amber
  vtsstation: '#c89348',
  bts: '#c89348',
  vts: '#c89348',
  company: '#6e7785', // graphite slate
  building: '#6e7785',
  address: '#c47a4f', // clay terracotta
  default: '#7d8290',
}

const ALIASES: Record<string, string> = {
  vehicle: 'vehicle',
  car: 'car',
  person: 'person',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'mobile',
  mobiledevice: 'mobiledevice',
  btsstation: 'btsstation',
  vtsstation: 'vtsstation',
  vts: 'vts',
  bts: 'bts',
  aircraft: 'aircraft',
  plane: 'aircraft',
  company: 'company',
  building: 'building',
  organization: 'company',
  address: 'address',
  location: 'address',
}

export function normalizeMarkerType(rawType: string | undefined | null): string {
  if (!rawType) return 'default'
  const k = rawType.toLowerCase().replace(/[\s_-]/g, '')
  return ALIASES[k] ?? (PASTEL_BY_TYPE[k] ? k : 'default')
}

export function pastelFor(type: string | undefined | null): string {
  return PASTEL_BY_TYPE[normalizeMarkerType(type)] ?? PASTEL_BY_TYPE.default
}

// Flat list of `[type, hex]` pairs in the order MapLibre expects for a
// `match` expression. Returned freshly each call (MapLibre style spec is
// not happy with shared references across calls).
export function buildTypeColorMatchPairs(): (string | string[])[] {
  const out: (string | string[])[] = []
  for (const [type, color] of Object.entries(PASTEL_BY_TYPE)) {
    if (type === 'default') continue
    out.push(type, color)
  }
  return out
}

// Cluster heat scale — yellow (small) → orange → red (large), modelled on
// USGS earthquake clusters. Used by the cluster circle layer step expression.
export const CLUSTER_HEAT = {
  small: 'rgba(253,224,71,0.9)', // yellow-300
  mid: 'rgba(251,146,60,0.9)', // orange-400
  large: 'rgba(239,68,68,0.9)', // red-500
  xlarge: 'rgba(185,28,28,0.92)', // red-700
} as const

// Per-entity track-line palette. The previous default was a single type-
// driven colour (every MobileDevice track came out the same blue), which
// made overlapping tracks indistinguishable. Spreading entity ids across a
// rotating palette of 12 high-contrast hues gives each trail its own
// identity on the map from the very first render — the user no longer has
// to hand-pick colours just to tell two tracks apart. Colours are picked
// for visibility on dark basemaps and to stay distinct under the soft
// glow underlay the tracks layer renders.
const TRACK_COLOR_PALETTE = [
  '#60a5fa', // blue-400
  '#a78bfa', // violet-400
  '#f472b6', // pink-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
  '#fb7185', // rose-400
  '#22d3ee', // cyan-400
  '#a3e635', // lime-400
  '#f97316', // orange-500
  '#c084fc', // purple-400
  '#facc15', // yellow-400
  '#2dd4bf', // teal-400
] as const

/**
 * Deterministic default track colour for a given entity id. Same id always
 * yields the same colour, so a track keeps its identity across reloads and
 * between the map line and the sidebar swatch. Override via the per-entity
 * `trackColors` store wins when the analyst has picked one explicitly.
 */
export function defaultTrackColorFor(id: string): string {
  // djb2-style 32-bit hash — small, deterministic, plenty of bit-mix for
  // a 12-slot palette. We avoid String.prototype.split + reduce to keep
  // allocation noise out of the maplibre render loop.
  let h = 5381
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) + h + id.charCodeAt(i)) | 0
  }
  const idx = Math.abs(h) % TRACK_COLOR_PALETTE.length
  return TRACK_COLOR_PALETTE[idx]
}
